// Would be nice to get this working for mobile, but html-to-image has significant issues on my mobile iOS devices related to image loading and rendering.
// For now, we will just provide desktop support and not attempt mobile capture.

import { toPng, getFontEmbedCSS } from 'html-to-image';

/**
 * Simple image loader for desktop browsers
 */
const loadImages = async (element) => {
    const images = element.querySelectorAll('img');
    const loadPromises = [];
    
    images.forEach(img => {
        if (!img.complete) {
            loadPromises.push(new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve; // Continue even if one fails
            }));
        }
    });
    
    if (loadPromises.length > 0) {
        await Promise.all(loadPromises);
    }
};

/**
 * Main capture function for desktop browsers
 */
export const captureElementAsImage = async (element) => {
    console.log('[DESKTOP] Starting desktop capture');
    
    try {
        // 1. Wait for images to load
        console.log('[DESKTOP] Loading images...');
        await loadImages(element);
        
        // 2. Wait for fonts (if available)
        if (document.fonts) {
            console.log('[DESKTOP] Waiting for fonts...');
            await document.fonts.ready;
        }
        
        // 3. Get font CSS if needed
        let fontEmbedCSS = '';
        try {
            fontEmbedCSS = await getFontEmbedCSS(element);
            console.log('[DESKTOP] Font CSS obtained');
        } catch (e) {
            console.warn('[DESKTOP] No font CSS:', e.message);
        }
        
        // 4. Capture with simple options
        console.log('[DESKTOP] Capturing element...');
        
        const dataUrl = await toPng(element, {
            pixelRatio: 1,
            backgroundColor: 'transparent',
            quality: 1.0,
            skipFonts: false,
            ...(fontEmbedCSS && { fontEmbedCSS })
        });
        
        console.log('[DESKTOP] Capture complete');
        
        // 5. Convert to blob
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        
        return blob;
        
    } catch (error) {
        console.error('[DESKTOP] Capture failed:', error);
        throw new Error(`Failed to capture image: ${error.message}`);
    }
};

/**
 * Save function optimized for desktop
 */
export const saveImageBlob = async (blob, fileName) => {
    console.log('[DESKTOP-SAVE] Saving image...');
    
    // Ensure .png extension
    let finalFileName = fileName.endsWith('.png') ? fileName : `${fileName}.png`;
    
    // 1. Try File System Access API (modern browsers)
    if (window.showSaveFilePicker) {
        try {
            const handle = await window.showSaveFilePicker({
                suggestedName: finalFileName,
                types: [{
                    description: 'PNG Image',
                    accept: { 'image/png': ['.png'] }
                }]
            });
            
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            
            console.log('[DESKTOP-SAVE] Saved via File System API');
            return { success: true, method: 'fileSystem' };
            
        } catch (err) {
            if (err.name === 'AbortError') {
                return { success: false, method: 'abort' };
            }
            console.warn('[DESKTOP-SAVE] File System API failed:', err.message);
        }
    }
    
    // 2. Fallback: Traditional download
    try {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = finalFileName;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);
        
        console.log('[DESKTOP-SAVE] Saved via download link');
        return { success: true, method: 'download' };
        
    } catch (err) {
        console.error('[DESKTOP-SAVE] Save failed:', err);
        throw new Error('Failed to save image');
    }
};